import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { 
  Mic, MicOff, Camera, Loader2, CheckCircle2, ArrowRight,
  Sparkles, Volume2, Upload, Zap, Building2, UtensilsCrossed,
  Clock, Calendar, ShoppingBag, Eye, Brain, Hand, Ear,
  WifiIcon, AlertCircle, ChevronRight, RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// POS Systems supported
const POS_SYSTEMS = [
  { id: "toast", name: "Toast", logo: "🍞", popular: true, status: "ready" },
  { id: "square", name: "Square", logo: "⬜", popular: true, status: "ready" },
  { id: "clover", name: "Clover", logo: "🍀", popular: true, status: "ready" },
  { id: "lightspeed", name: "Lightspeed", logo: "⚡", popular: true, status: "ready" },
  { id: "touchbistro", name: "TouchBistro", logo: "👆", popular: false, status: "ready" },
  { id: "revel", name: "Revel", logo: "📱", popular: false, status: "ready" },
  { id: "aloha", name: "Aloha (NCR)", logo: "🌺", popular: false, status: "ready" },
  { id: "spoton", name: "SpotOn", logo: "📍", popular: false, status: "ready" },
  { id: "upserve", name: "Upserve", logo: "📊", popular: false, status: "ready" },
  { id: "other", name: "Other POS", logo: "🔌", popular: false, status: "manual" },
  { id: "none", name: "No POS Yet", logo: "📝", popular: false, status: "manual" },
];

// Supervisor personas
const SUPERVISORS = {
  echo: { name: "Echo", role: "The Ears", icon: Ear, color: "emerald" },
  apollo: { name: "Apollo", role: "The Eyes", icon: Eye, color: "blue" },
  athena: { name: "Athena", role: "The Brain", icon: Brain, color: "purple" },
  atlas: { name: "Atlas", role: "The Hands", icon: Hand, color: "orange" },
};

// Voice recording hook
const useVoiceRecording = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [transcriptionError, setTranscriptionError] = useState("");
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream);
      audioChunks.current = [];

      mediaRecorder.current.ondataavailable = (e) => {
        audioChunks.current.push(e.data);
      };

      mediaRecorder.current.onstop = async () => {
        const audioBlob = new Blob(audioChunks.current, { type: "audio/webm" });
        const formData = new FormData();
        formData.append("audio", audioBlob, "recording.webm");

        // Read token at call-time to avoid stale closure; S003 canonical key
        const token =
          sessionStorage.getItem("argus_trial_token") ||
          localStorage.getItem("admin_token");

        setIsTranscribing(true);
        setTranscriptionError("");
        try {
          const res = await fetch(`${API_URL}/api/voice/transcribe`, {
            method: "POST",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
          });
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.detail || `Transcription failed (${res.status})`);
          }
          const data = await res.json();
          if (data.text) {
            setTranscript(data.text);
          } else {
            setTranscriptionError("No speech detected. Please try again.");
          }
        } catch (err) {
          console.error("Transcription error:", err);
          setTranscriptionError(err.message || "Transcription failed. Please try again.");
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorder.current.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Microphone access denied:", err);
      toast.error("Please allow microphone access");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.stop();
      mediaRecorder.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  return {
    isRecording,
    isTranscribing,
    transcript,
    transcriptionError,
    setTranscript,
    startRecording,
    stopRecording,
  };
};

// AI Chat Bubble Component
const AIChatBubble = ({ supervisor, message, isTyping }) => {
  const sup = SUPERVISORS[supervisor];
  const Icon = sup?.icon || Sparkles;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex gap-3 mb-4"
    >
      <div className={`w-10 h-10 rounded-full bg-${sup?.color || 'gold'}-500/20 flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 text-${sup?.color || 'gold'}-400`} />
      </div>
      <div className="flex-1">
        <p className={`text-xs text-${sup?.color || 'gold'}-400 font-medium mb-1`}>
          {sup?.name || 'Argus'} • {sup?.role || 'AI Assistant'}
        </p>
        <div className="bg-indigo-950/50 border border-gold/10 rounded-xl rounded-tl-none p-4">
          {isTyping ? (
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-gold/50 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-2 h-2 bg-gold/50 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-2 h-2 bg-gold/50 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          ) : (
            <p className="text-white text-sm leading-relaxed">{message}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// User Response Bubble
const UserBubble = ({ message }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex justify-end mb-4"
  >
    <div className="bg-gold/20 border border-gold/30 rounded-xl rounded-tr-none p-4 max-w-[80%]">
      <p className="text-white text-sm">{message}</p>
    </div>
  </motion.div>
);

// Voice Input Button
const VoiceInputButton = ({ isRecording, onStart, onStop, disabled }) => (
  <motion.button
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    onClick={isRecording ? onStop : onStart}
    disabled={disabled}
    className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
      isRecording 
        ? "bg-red-500 animate-pulse shadow-[0_0_30px_rgba(239,68,68,0.5)]" 
        : "bg-gold hover:bg-gold/90 shadow-[0_0_30px_rgba(212,175,55,0.3)]"
    } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
  >
    {isRecording ? (
      <MicOff className="w-8 h-8 text-white" />
    ) : (
      <Mic className="w-8 h-8 text-indigo-900" />
    )}
  </motion.button>
);

// Main AI Onboarding Component
export default function AIOnboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState("welcome"); // welcome, voice-intro, pos-select, pos-connect, menu-scan, review
  const [isProcessing, setIsProcessing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [restaurantData, setRestaurantData] = useState({
    name: "",
    description: "",
    cuisineType: "",
    address: "",
    phone: "",
    hours: {},
    menu: { categories: [] },
    posSystem: null,
    posConnected: false,
  });
  
  const { isRecording, isTranscribing, transcript, transcriptionError, setTranscript, startRecording, stopRecording } = useVoiceRecording();
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  // Always-current ref for processVoiceInput — prevents stale closure in useEffect
  const processVoiceInputRef = useRef(null);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // State-driven trigger: when transcript is set by the hook, process it
  useEffect(() => {
    if (transcript && processVoiceInputRef.current) {
      processVoiceInputRef.current(transcript);
    }
  }, [transcript]);

  // Surface transcription errors as toasts (never silent)
  useEffect(() => {
    if (transcriptionError) {
      toast.error(transcriptionError);
    }
  }, [transcriptionError]);

  // Add message helper
  const addMessage = (supervisor, message, isUser = false) => {
    setMessages(prev => [...prev, { supervisor, message, isUser, id: Date.now() }]);
  };

  // Process voice input with AI
  const processVoiceInput = async (text) => {
    if (!text.trim()) return;
    
    addMessage(null, text, true);
    setIsProcessing(true);
    
    try {
      const token = sessionStorage.getItem("argus_trial_token") || localStorage.getItem("admin_token");
      const res = await fetch(`${API_URL}/api/onboarding/ai-process`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          step,
          userInput: text,
          currentData: restaurantData,
        })
      });
      
      const data = await res.json();
      
      if (data.extractedData) {
        setRestaurantData(prev => ({ ...prev, ...data.extractedData }));
      }
      
      if (data.response) {
        addMessage(data.supervisor || "echo", data.response);
      }
      
      if (data.nextStep) {
        setTimeout(() => setStep(data.nextStep), 1500);
      }
    } catch (err) {
      console.error("AI processing error:", err);
      addMessage("athena", "I had trouble understanding that. Could you try again?");
    } finally {
      setIsProcessing(false);
      setTranscript("");
    }
  };
  // Keep the ref current so the transcript useEffect always calls the latest version
  processVoiceInputRef.current = processVoiceInput;

  // Handle menu photo upload
  const handleMenuUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    
    setIsProcessing(true);
    addMessage("apollo", "Let me analyze your menu...");
    
    const formData = new FormData();
    for (let file of files) {
      formData.append("images", file);
    }
    
    try {
      const token = sessionStorage.getItem("argus_trial_token") || localStorage.getItem("admin_token");
      const res = await fetch(`${API_URL}/api/onboarding/scan-menu`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData,
      });
      
      const data = await res.json();
      
      if (data.menu) {
        setRestaurantData(prev => ({ ...prev, menu: data.menu }));
        addMessage("apollo", `I found ${data.itemCount} menu items across ${data.categoryCount} categories. Let me show Athena...`);
        
        setTimeout(() => {
          addMessage("athena", `I've organized everything. You have ${data.menu.categories.map(c => c.name).join(", ")}. Should I make any changes?`);
        }, 2000);
      }
    } catch (err) {
      console.error("Menu scan error:", err);
      addMessage("apollo", "I had trouble reading the menu. Could you try a clearer photo?");
    } finally {
      setIsProcessing(false);
    }
  };

  // Connect to POS system
  const connectPOS = async (posId) => {
    const pos = POS_SYSTEMS.find(p => p.id === posId);
    setRestaurantData(prev => ({ ...prev, posSystem: pos }));
    
    if (pos.status === "manual") {
      addMessage("atlas", `No problem! We'll continue without a POS connection. You can connect your POS to Argus after onboarding is complete.`);
      setTimeout(() => setStep("voice-intro"), 1500);
      return;
    }
    
    // Record POS selection and move forward — no fake OAuth simulation
    addMessage("atlas", `Got it — ${pos.name} noted. POS sync will be available once integration is configured after onboarding. Let's learn about your restaurant first.`);
    setTimeout(() => {
      addMessage("echo", "Tell me about your restaurant. Just talk naturally!");
      setStep("voice-intro");
    }, 2000);
  };

  // Render step content
  const renderStep = () => {
    switch (step) {
      case "welcome":
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="flex justify-center gap-4 mb-8">
              {Object.values(SUPERVISORS).map((sup, i) => {
                const Icon = sup.icon;
                return (
                  <motion.div
                    key={sup.name}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.1 }}
                    className={`w-16 h-16 rounded-full bg-${sup.color}-500/20 flex items-center justify-center`}
                  >
                    <Icon className={`w-8 h-8 text-${sup.color}-400`} />
                  </motion.div>
                );
              })}
            </div>
            
            <h1 className="text-3xl font-black text-white mb-4">
              Meet Your AI Team
            </h1>
            <p className="text-slate-400 mb-8 max-w-md mx-auto">
              Echo, Apollo, Athena, and Atlas will set up your restaurant in minutes. 
              No typing required — just talk, snap photos, and connect.
            </p>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                addMessage("echo", "Hey there! I'm Echo, your AI receptionist. Let's get your restaurant set up. First, what POS system do you use?");
                setStep("pos-select");
              }}
              className="px-8 py-4 bg-gold text-indigo-900 rounded-xl font-bold text-lg flex items-center gap-2 mx-auto hover:bg-gold/90 transition-colors"
            >
              <Sparkles className="w-5 h-5" />
              Let's Get Started
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        );

      case "pos-select":
        return (
          <div>
            <div className="mb-6 max-h-64 overflow-y-auto">
              {messages.map(msg => 
                msg.isUser ? (
                  <UserBubble key={msg.id} message={msg.message} />
                ) : (
                  <AIChatBubble key={msg.id} supervisor={msg.supervisor} message={msg.message} />
                )
              )}
              <div ref={chatEndRef} />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {POS_SYSTEMS.filter(p => p.popular).map(pos => (
                <motion.button
                  key={pos.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => connectPOS(pos.id)}
                  disabled={isProcessing}
                  className="p-4 bg-indigo-950/50 border border-gold/20 rounded-xl hover:border-gold/50 transition-all text-left"
                >
                  <span className="text-2xl mb-2 block">{pos.logo}</span>
                  <p className="text-white font-medium">{pos.name}</p>
                  <p className="text-xs text-slate-500 mt-1">Coming soon</p>
                </motion.button>
              ))}
            </div>
            
            <div className="mt-4 flex flex-wrap gap-2">
              {POS_SYSTEMS.filter(p => !p.popular).map(pos => (
                <button
                  key={pos.id}
                  onClick={() => connectPOS(pos.id)}
                  disabled={isProcessing}
                  className="px-3 py-2 bg-indigo-950/30 border border-gold/10 rounded-lg text-sm text-slate-400 hover:text-white hover:border-gold/30 transition-all"
                >
                  {pos.logo} {pos.name}
                </button>
              ))}
            </div>
          </div>
        );

      case "voice-intro":
        return (
          <div>
            <div className="mb-6 max-h-64 overflow-y-auto">
              {messages.map(msg => 
                msg.isUser ? (
                  <UserBubble key={msg.id} message={msg.message} />
                ) : (
                  <AIChatBubble key={msg.id} supervisor={msg.supervisor} message={msg.message} />
                )
              )}
              {isProcessing && <AIChatBubble supervisor="athena" isTyping />}
              <div ref={chatEndRef} />
            </div>
            
            <div className="text-center">
              <p className="text-slate-400 text-sm mb-4">
                {isTranscribing
                  ? "Transcribing..."
                  : isRecording
                  ? "Listening... tap to stop"
                  : "Tap to speak"}
              </p>
              
              <VoiceInputButton
                isRecording={isRecording}
                onStart={startRecording}
                onStop={stopRecording}
                disabled={isProcessing || isTranscribing}
              />
              
              {isTranscribing && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-4 flex items-center justify-center gap-2 text-gold text-sm"
                >
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Transcribing your voice...
                </motion.div>
              )}
              
              {transcript && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-4 p-3 bg-indigo-950/50 rounded-lg"
                >
                  <p className="text-white text-sm">{transcript}</p>
                </motion.div>
              )}
              
              <div className="mt-6 flex justify-center gap-4">
                <button
                  onClick={() => setStep("menu-scan")}
                  className="px-4 py-2 text-gold text-sm flex items-center gap-2 hover:underline"
                >
                  <Camera className="w-4 h-4" />
                  Scan menu instead
                </button>
                <button
                  onClick={() => setStep("review")}
                  className="px-4 py-2 text-slate-400 text-sm flex items-center gap-2 hover:text-white"
                >
                  Skip to review
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        );

      case "menu-scan":
        return (
          <div>
            <div className="mb-6 max-h-48 overflow-y-auto">
              {messages.slice(-3).map(msg => 
                msg.isUser ? (
                  <UserBubble key={msg.id} message={msg.message} />
                ) : (
                  <AIChatBubble key={msg.id} supervisor={msg.supervisor} message={msg.message} />
                )
              )}
              {isProcessing && <AIChatBubble supervisor="apollo" isTyping />}
              <div ref={chatEndRef} />
            </div>
            
            <div className="text-center">
              <AIChatBubble 
                supervisor="apollo" 
                message="Show me your menu! Take photos of each page and I'll extract all the items, prices, and descriptions automatically." 
              />
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleMenuUpload}
                accept="image/*"
                multiple
                className="hidden"
              />
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessing}
                className="mt-4 px-8 py-4 bg-blue-500 text-white rounded-xl font-bold flex items-center gap-2 mx-auto hover:bg-blue-600 transition-colors"
              >
                {isProcessing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Camera className="w-5 h-5" />
                )}
                {isProcessing ? "Analyzing..." : "Upload Menu Photos"}
              </motion.button>
              
              <p className="text-slate-500 text-sm mt-4">
                Supports JPG, PNG • Multiple pages OK
              </p>
              
              <button
                onClick={() => setStep("review")}
                className="mt-6 px-4 py-2 text-slate-400 text-sm hover:text-white"
              >
                Skip for now →
              </button>
            </div>
          </div>
        );

      case "review":
        return (
          <div>
            <AIChatBubble 
              supervisor="athena" 
              message="Here's what I've learned about your restaurant. Everything look good?" 
            />
            
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="p-4 bg-indigo-950/30 rounded-xl border border-gold/10">
                <Building2 className="w-6 h-6 text-gold mb-2" />
                <p className="text-white font-medium">{restaurantData.name || "Not set"}</p>
                <p className="text-slate-400 text-sm">{restaurantData.cuisineType || "Cuisine type"}</p>
              </div>
              
              <div className="p-4 bg-indigo-950/30 rounded-xl border border-gold/10">
                <UtensilsCrossed className="w-6 h-6 text-gold mb-2" />
                <p className="text-white font-medium">{restaurantData.menu?.categories?.length || 0} Categories</p>
                <p className="text-slate-400 text-sm">
                  {restaurantData.menu?.categories?.reduce((acc, c) => acc + (c.items?.length || 0), 0) || 0} Menu items
                </p>
              </div>
              
              <div className="p-4 bg-indigo-950/30 rounded-xl border border-gold/10">
                <Zap className="w-6 h-6 text-gold mb-2" />
                <p className="text-white font-medium">{restaurantData.posSystem?.name || "No POS"}</p>
                <p className={`text-sm ${restaurantData.posConnected ? "text-emerald-400" : "text-slate-400"}`}>
                  {restaurantData.posConnected ? "✓ Connected" : "Not connected"}
                </p>
              </div>
              
              <div className="p-4 bg-indigo-950/30 rounded-xl border border-gold/10">
                <Clock className="w-6 h-6 text-gold mb-2" />
                <p className="text-white font-medium">Hours</p>
                <p className="text-slate-400 text-sm">
                  {Object.keys(restaurantData.hours).length > 0 ? "Configured" : "Not set"}
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex gap-4">
              <button
                onClick={() => setStep("voice-intro")}
                className="flex-1 px-6 py-3 bg-indigo-900/50 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-indigo-900 transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
                Add More Info
              </button>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={async () => {
                  setIsProcessing(true);
                  // Save all data
                  const token = sessionStorage.getItem("argus_trial_token") || localStorage.getItem("admin_token");
                  await fetch(`${API_URL}/api/restaurant/onboarding`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      "Authorization": `Bearer ${token}`
                    },
                    body: JSON.stringify({
                      step: 6,
                      profile: {
                        name: restaurantData.name,
                        description: restaurantData.description,
                        cuisineType: restaurantData.cuisineType,
                        address: restaurantData.address,
                        phone: restaurantData.phone,
                      },
                      hours: restaurantData.hours,
                      menu: restaurantData.menu,
                      reservations: { enabled: true },
                      takeout: { enabled: true, preOrderEnabled: true },
                    })
                  });
                  
                  // Activate restaurant
                  await fetch(`${API_URL}/api/restaurant/activate`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      "Authorization": `Bearer ${token}`
                    }
                  });
                  
                  toast.success("Supervisors activated!");
                  navigate("/trial/dashboard");
                }}
                disabled={isProcessing}
                className="flex-1 px-6 py-3 bg-emerald-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors"
              >
                {isProcessing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-5 h-5" />
                )}
                Activate Supervisors
              </motion.button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#030B2C] flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* Progress indicator */}
        <div className="flex justify-center gap-2 mb-8">
          {["welcome", "pos-select", "voice-intro", "menu-scan", "review"].map((s, i) => (
            <div
              key={s}
              className={`w-2 h-2 rounded-full transition-colors ${
                step === s ? "bg-gold" : 
                ["welcome", "pos-select", "voice-intro", "menu-scan", "review"].indexOf(step) > i 
                  ? "bg-emerald-500" 
                  : "bg-indigo-900/50"
              }`}
            />
          ))}
        </div>
        
        {/* Main content */}
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="bg-indigo-950/20 border border-gold/10 rounded-2xl p-8"
        >
          {renderStep()}
        </motion.div>
      </div>
    </div>
  );
}
